import { useState, useEffect } from 'react';
import axios from 'axios';
import { Shield, AlertTriangle, Activity, Globe, Zap } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ThreatMonitor() {
  const [threats, setThreats] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchThreats();
    const interval = setInterval(fetchThreats, 10000); // Refresh every 10 seconds
    return () => clearInterval(interval);
  }, []);

  const fetchThreats = async () => {
    try {
      const response = await axios.get(`${API}/security/threats`);
      setThreats(response.data.threats);
      setStats(response.data.stats);
    } catch (error) {
      console.error('Failed to fetch threats:', error);
    } finally {
      setLoading(false);
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'critical': return 'bg-red-500/20 text-red-400 border-red-500/30';
      case 'high': return 'bg-orange-500/20 text-orange-400 border-orange-500/30';
      case 'medium': return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
      default: return 'bg-green-500/20 text-green-400 border-green-500/30';
    }
  };

  const getTimeAgo = (timestamp) => {
    const seconds = Math.floor((new Date() - new Date(timestamp)) / 1000);
    if (seconds < 60) return `${seconds}s ago`;
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    return `${Math.floor(seconds / 3600)}h ago`;
  };

  return (
    <div className="bg-[#0a0a0a] border border-[#262626] rounded-xl overflow-hidden" data-testid="threat-monitor">
      {/* Header */}
      <div className="p-4 border-b border-[#262626] flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-red-500/10 flex items-center justify-center">
            <Activity className="w-4 h-4 text-red-400" />
          </div>
          <div>
            <h3 className="font-medium text-white text-sm">Threat Monitor</h3>
            <p className="text-xs text-gray-500">Real-time protection</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
          <span className="text-xs text-green-400 font-mono">ACTIVE</span>
        </div>
      </div>

      {/* Stats Bar */}
      {stats && (
        <div className="grid grid-cols-3 gap-px bg-[#262626]">
          <div className="bg-[#0a0a0a] p-3 text-center">
            <div className="font-mono text-lg text-[#00FF94]">{stats.blocked_today}</div>
            <div className="text-xs text-gray-500">Today</div>
          </div>
          <div className="bg-[#0a0a0a] p-3 text-center">
            <div className="font-mono text-lg text-white">{stats.blocked_this_week}</div>
            <div className="text-xs text-gray-500">This Week</div>
          </div>
          <div className="bg-[#0a0a0a] p-3 text-center">
            <div className="font-mono text-lg text-white">{(stats.blocked_total / 1000).toFixed(1)}K</div>
            <div className="text-xs text-gray-500">Total</div>
          </div>
        </div>
      )}

      {/* Threat Feed */}
      <div className="max-h-[300px] overflow-y-auto">
        {threats.slice(0, 8).map((threat, index) => (
          <div 
            key={threat.id}
            className="p-3 border-b border-[#262626] hover:bg-[#171717] transition-colors flex items-center gap-3"
            style={{ animationDelay: `${index * 0.1}s` }}
          >
            {/* Severity Indicator */}
            <div className={`w-2 h-2 rounded-full ${
              threat.severity === 'critical' ? 'bg-red-500' :
              threat.severity === 'high' ? 'bg-orange-500' :
              threat.severity === 'medium' ? 'bg-yellow-500' : 'bg-green-500'
            }`} />
            
            {/* Content */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="text-sm text-white truncate">{threat.description}</span>
                <span className={`px-1.5 py-0.5 rounded text-xs border ${getSeverityColor(threat.severity)}`}>
                  {threat.severity}
                </span>
              </div>
              <div className="flex items-center gap-2 text-xs text-gray-500 mt-0.5">
                <Globe className="w-3 h-3" />
                <span>{threat.ip_origin}</span>
                <span>•</span>
                <span>{threat.country}</span>
              </div>
            </div>
            
            {/* Time & Status */}
            <div className="text-right flex-shrink-0">
              <div className="text-xs text-green-400 font-mono">BLOCKED</div>
              <div className="text-xs text-gray-500">{getTimeAgo(threat.timestamp)}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Footer */}
      <div className="p-3 bg-[#050505] border-t border-[#262626] flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Shield className="w-4 h-4 text-[#00FF94]" />
          <span className="text-xs text-gray-400">Protection: <span className="text-[#00FF94]">Maximum</span></span>
        </div>
        <div className={`px-2 py-1 rounded text-xs font-mono border ${
          stats?.threat_level === 'high' ? 'bg-red-500/10 text-red-400 border-red-500/30' :
          stats?.threat_level === 'medium' ? 'bg-yellow-500/10 text-yellow-400 border-yellow-500/30' :
          'bg-green-500/10 text-green-400 border-green-500/30'
        }`}>
          THREAT LEVEL: {stats?.threat_level?.toUpperCase() || 'LOW'}
        </div>
      </div>
    </div>
  );
}
